import { useState, useRef, useEffect, useMemo, MutableRefObject } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { fetchAllRows } from '@/lib/supabaseHelpers';
import { getUtmParams, getDeviceType } from './useQuizTracking';
import type { FlowStep } from '@/types/flow';

type QuizScreen = { id: string; steps: FlowStep[] };

export function useABTest(rawScreens: QuizScreen[], sessionIdRef: MutableRefObject<string>) {
  const [abVariant, setAbVariant] = useState<'A' | 'B'>('A');
  const abVariantRef = useRef<'A' | 'B'>('A');
  const abAssignedRef = useRef(false);

  const screens = useMemo(() => {
    // Variant A: unchanged (no social proof)
    if (abVariant === 'A') return rawScreens;

    // Variant B: insert social proof text between a2 (subtitle) and a3 (age question) on screen 1
    const screen1 = rawScreens[0];
    if (!screen1) return rawScreens;

    const a2Index = screen1.steps.findIndex(s => s.id === 'a2');
    if (a2Index === -1) return rawScreens;

    const socialProofStep: FlowStep = {
      id: 'a2_social_proof',
      type: 'text',
      title: '✅ 5.800+ vrouwen gingen je voor',
      screenId: screen1.steps[0]?.screenId,
      settings: { align: 'center' },
      design: { fontSize: 14, fontSizeMobile: 13, fontWeight: '600', color: '#16a34a', margin: '4px 0 0 0' },
    };

    const newScreen1 = {
      ...screen1,
      steps: [
        ...screen1.steps.slice(0, a2Index + 1),
        socialProofStep,
        ...screen1.steps.slice(a2Index + 1),
      ],
    };

    return [newScreen1, ...rawScreens.slice(1)];
  }, [rawScreens, abVariant]);

  useEffect(() => {
    const assignAndTrack = async () => {
      if (!abAssignedRef.current) {
        // Get active test start time for scoped counter-balancing
        const { data: activeTest } = await supabase
          .from('ab_test_log')
          .select('started_at')
          .eq('status', 'active')
          .order('created_at', { ascending: false })
          .limit(1)
          .single();

        if (activeTest?.started_at) {
          const counts = await fetchAllRows<{ ab_variant: string | null }>(
            'quiz_pageviews', 'ab_variant',
            [
              { column: 'ab_variant', operator: 'not.is', value: null },
              { column: 'utm_source', operator: 'not.is', value: null },
              { column: 'created_at', operator: 'gte', value: activeTest.started_at },
            ]
          );
          const countA = counts.filter(r => r.ab_variant === 'A').length;
          const countB = counts.filter(r => r.ab_variant === 'B').length;
          const chosen = countA <= countB ? 'A' : 'B';
          abVariantRef.current = chosen;
          setAbVariant(chosen);
        } else {
          // No active test — random 50/50
          const chosen = Math.random() < 0.5 ? 'A' : 'B';
          abVariantRef.current = chosen;
          setAbVariant(chosen);
        }
        abAssignedRef.current = true;
      }

      const utm = getUtmParams();
      const device = getDeviceType();
      supabase.from('quiz_pageviews').insert({
        session_id: sessionIdRef.current,
        device_type: device,
        utm_source: utm.utmSource || null,
        ab_variant: abVariantRef.current,
        user_agent: navigator.userAgent || null,
      }).then(() => {});
      supabase.from('quiz_sessions').upsert({
        session_id: sessionIdRef.current,
        current_step: 1,
        device_type: device,
        is_completed: false,
        utm_source: utm.utmSource || null,
        utm_medium: utm.utmMedium || null,
        utm_campaign: utm.utmCampaign || null,
        ab_variant: abVariantRef.current,
      }, { onConflict: 'session_id' }).then(() => {});
    };
    assignAndTrack();
  }, []);

  return { screens, abVariant, abVariantRef };
}
